import {ChangeDetectionStrategy, Component, computed, input, model, output} from '@angular/core';
import {
  BerthVisitSearch,
  CancelVisit,
  LastEditedBy,
  VisitSummary,
  VisitSummaryStatus
} from "@portbase/bezoekschip-service-typescriptmodels";
import {AppContext} from "../../../app-context";
import {VisitOverviewUtils} from "./visit-overview.utils";
import {openConfirmationModalWithCallback, sendCommand} from "../../../common/utils";
import {ModalConfirmAutofocus, ModalConfirmAutofocusData} from "../../../common/modal/modal-confirm.component";
import {ClearanceService} from "../../../visit-details/visit/clearance/clearance-service";
import {PortvisitUtils} from "../../../refdata/portvisit-utils";
import {DateRange} from "../../../common/date/date-range/date-range";
import {ArrivalStatus, MovementInfo} from "../../common/movement-icons/movement-icons.component";
import {SentenceCasePipe} from "../../../common/sentence-case.pipe";

@Component({
    selector: 'app-visit-overview-item',
    templateUrl: './visit-overview-item.component.html',
    styleUrls: ['./visit-overview-item.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VisitOverviewItemComponent {
  utils = VisitOverviewUtils;
  appContext = AppContext;
  portVisitUtils = PortvisitUtils;

  visitSummary = model<NewVisitSummary>(undefined, { alias: 'summary' });
  term = input<string>();

  zoomVesselImage = output<NewVisitSummary>();

  entryDate = computed(() => this.visitSummary().ataPort || this.visitSummary().etaPort);
  exitDate = computed(() => this.visitSummary().atdPort || this.visitSummary().etdPort);
  entryPointName = computed(() =>
    this.visitSummary().entryPoint?.name || 'No entry point');
  exitPointName = computed(() =>
    this.visitSummary().exitPoint?.name || 'No exit point');
  expected = computed(() => [
    VisitSummaryStatus.DRAFT, VisitSummaryStatus.PLANNED, VisitSummaryStatus.EXPECTED
  ].includes(this.visitSummary().visitStatus));
  areNauticalServicesApplicable = computed(() =>
    this.portVisitUtils.areNauticalServicesApplicable(this.visitSummary().portUnCode));
  leastTerminalStatus = computed(() =>
    this.utils.getLeastTerminalStatus(this.visitSummary(), this.clearanceService));
  movements = computed(() => {
    const summary = this.visitSummary();
    const movements: MovementInfo[] = [];
    movements.push({
      name: SentenceCasePipe.format(this.entryPointName()),
      arrivalStatus: summary.ataPort ? ArrivalStatus.arrived : ArrivalStatus.expected,
      date: this.entryDate()
    });
    summary.berthVisitSearches.forEach(bv => {
      movements.push({
        name: SentenceCasePipe.format(bv.name || bv.terminalName || bv.stevedore || 'No berth(s)'),
        arrivalStatus: bv.atd ? ArrivalStatus.departed : bv.ata ? ArrivalStatus.arrived : ArrivalStatus.expected,
        date: bv.ata || bv.atd || bv.eta || bv.etd
      })
    })
    movements.push({
      name: SentenceCasePipe.format(this.exitPointName()),
      arrivalStatus: summary.atdPort ? ArrivalStatus.arrived : ArrivalStatus.expected,
      date: this.exitDate()
    });
    return movements;
  });

  constructor(protected clearanceService: ClearanceService) {
  }

  isOrganisationCurrentVisitDeclarant = computed(() => {
    return AppContext.isAdmin()
      || this.visitSummary().declarantShortName === AppContext.userProfile.organisation?.shortName
      || this.visitSummary().ownerShortName === AppContext.userProfile.organisation?.shortName;
  });

  isStevedorePlannerAtAnyBerthVisit = computed(() => {
    return this.visitSummary().berthVisitSearches?.some(bv => AppContext.isStevedorePlannerFor(bv.stevedoreShortName));
  });

  showViewInboundCargo = computed(() => {
    const summary = this.visitSummary();
    return AppContext.isAdminOrCustoms() || (AppContext.isCargoImportViewer()
      && AppContext.cargoDeclarantUsesNewCargoScreenForCrn(AppContext.userProfile?.organisation?.shortName, summary.crn));
  });

  navigationUrl = computed(() => {
      return '/details/' + this.visitSummary().crn;
  });

  declarationTypesConsideringICS2 = computed(() => {
    return this.appContext.ics2ActivatedForOrganisationOrItsCrn(this.visitSummary().declarantShortName, this.visitSummary().crn) && this.hasNoPreIcs2Cargo()
      ? ['NOA', 'NOD', 'STO'] :  ['NOA', 'NOD', 'STO', 'SDT'];
  });

  tryRefreshVisitSummary = () => {
    sendCommand('com.portbase.bezoekschip.common.api.visit.RefreshVisitSummary', {
      crn: this.visitSummary().crn
    }, (summary: VisitSummary) => {
      this.visitSummary.set(VisitOverviewUtils.toExtendedSummary(summary, this.clearanceService));
      AppContext.registerSuccess('Visit summary has been rebuild successfully.');
    });
  }

  cancelVisit = () => {
    openConfirmationModalWithCallback((confirmed) => {
      if (confirmed) {
        this.doCancelVisit();
      }
    }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
      type: "danger",
      title: "Danger",
      message: "Once a visit is cancelled it is no longer possible to make changes.",
      question: "Are you sure that you want to cancel this visit?",
      confirmText: "Yes",
      cancelText: "No"
    }, 'static');
  }

  doCancelVisit = () => {
    sendCommand('com.portbase.bezoekschip.common.api.visit.CancelVisit', <CancelVisit>{
      crn: this.visitSummary().crn
    }, () => {
      this.visitSummary.update(summary => {
        return {
          ...summary,
          cancelled: true,
          visitStatus: VisitSummaryStatus.CANCELLED
        };
      });
      AppContext.registerSuccess('The visit was cancelled successfully.');
    });
  }

  popupStatusIndicator(statusIndicatorButtonId: string): void {
    document.getElementById(statusIndicatorButtonId).click();
  }

  popupServiceStatus(serviceStatusButtonId: string): void {
    document.getElementById(serviceStatusButtonId).click();
  }

  hoverStatusIndicatorSpan(statusIndicatorSpanId: string, hover: boolean): void {
    const element = document.getElementById(statusIndicatorSpanId);
    if (hover) {
      element.style.textDecoration = 'underline';
    }
    else {
      element.style.textDecoration = 'none';
    }
  }

  private hasNoPreIcs2Cargo() {
    return this.visitSummary().cargoAgentStatuses[this.visitSummary().declarantShortName]?.cargoImportStatus == null;
  }
}

export interface NewVisitSummary extends VisitSummaryExpanded {
  terminalPlanningEnabled?: boolean;
  currentBerthVisit?: BerthVisitSearch;
  lastEditedByUser?: LastEditedBy;
}
interface OverviewFilter {
  dateRange: DateRange;
  searchTerm;
  ownCallsOnly;
}

interface OverviewCategory {
  name: string,
  property: string,
  showOnLoad: boolean,
  visible: boolean
}

interface OpenTask {
  color: string,
  icon: string,
  tooltip: string
}

export interface VisitSummaryExpanded extends VisitSummary {
  calculatedOpenTasks?: OpenTask[];
  vesselImageUrl?: string;
}
